import React, { useState } from 'react';
import { Button, InputGroup, InputGroupText, Input, Label, Modal, ModalBody, ModalHeader, ModalFooter, Row, Col } from 'reactstrap';
import ReactQuill from 'react-quill';
import GridHeader from '../../components/common/GridHeader';
import JournalTypes from '../../components/common/LookupDropDown';
import moment from 'moment';
import 'react-quill/dist/quill.snow.css';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-balham.css';
import DeleteConfirmation from '../MultiDeleteConfirmation';
import { DataGrid } from '@mui/x-data-grid';

const Journal = (props) => {
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const [deleteConfirmationState, setDeleteConfirmationState] = useState({
        display: false,
        toDelete: [],
    });
    const [showAddEntry, setShowAddEntry] = useState(false);
    const [deleteEnabled, setDeleteEnabled] = useState(false);
    const [journalTypeId, setJournalTypeId] = useState();
    const [journalStatusTypeId, setJournalStatusTypeId] = useState();
    const [subject, setSubject] = useState(null);
    const [body, setBody] = useState(null);
    const [id, setId] = useState(null);
    const [disableDelete, setDisableDelete] = useState(true);
    const [subjectChanged, setSubjectChanged] = useState(false);
    const [activityDate, setActivityDate] = useState(moment());

    const handleToggleModal = () => {
        setJournalTypeId()
        setJournalStatusTypeId()
        setActivityDate(moment())
        setShowAddEntry(!showAddEntry);
    };

    const handleAddClick = () => {
        setShowDeleteConfirmation(false);
        setShowAddEntry(true);
        setSubject(null);
        setBody(null);
        setId(null);
    };

    const handleDeleteClick = (e) => {
        setDeleteConfirmationState({
            display: true,
            toDelete: deleteConfirmationState.toDelete
        });
    }

    const handleDelete = () => {
        props.onDelete(deleteConfirmationState.toDelete.map(x => x.id), props.currentSessionId);
        setDeleteConfirmationState({
            display: false,
            toDelete: [],
        });
    }

    const handleSaveEntry = () => {
        handleToggleModal();
        if (id) {
            props.onSave({ id: id, subject: subject, body: body, journalTypeId: journalTypeId, journalStatusTypeId: journalStatusTypeId, activityDate: activityDate });
            if (subjectChanged) {
                setDisableDelete(true);
            } else {
                setDisableDelete(false);
            }
            setSubjectChanged(false);
        } else {
            props.onSave({ subject: subject, body: body, journalTypeId: journalTypeId, activityDate: activityDate, journalStatusTypeId: journalStatusTypeId });
        }
    };

    const handleSelect = (e) => {
        props.onGet(e.id).then((res) => {
            setId(res.id)
            setJournalTypeId(res.journalTypeId)
            setJournalStatusTypeId(res.journalStatusTypeId)
            setSubject(res.subject);
            setBody(res.body);
            setShowAddEntry(true);
            setActivityDate(res.activityDate);
        });
    };

    const handleDeleteConfirmationToggle = () => setDeleteConfirmationState({
        display: !deleteConfirmationState.display,
        toDelete: deleteConfirmationState.toDelete
    });

    const handleSubjectChange = (e) => {
        setSubject(e.currentTarget.value);
        setSubjectChanged(true);
    };

    const handleBodyChange = (body) => {
        setBody(body);
    };

    const handleSelectionChanged = (e) => {
        if (e.length > 0) {
            setDeleteConfirmationState({
                display: false,
                toDelete: props.data.filter(x => e.find(y => y === x.id)),
            });
            setDeleteEnabled(true);
        }
        else
            setDeleteEnabled(false);
    }

    const handleOnChange = (event) => {
        const { target: { value } } = event
        setJournalTypeId(value);
    };

    const handleOnChangeStatusType = (event) => {
        const { target: { value } } = event
        setJournalStatusTypeId(value);
    };

    const handleActivityDateChange = (e) => {
        const { value } = e.target;
        setActivityDate(value)
    }
    return (
        <React.Fragment>
            <DeleteConfirmation
                title={`Delete Entry(s)`}
                onDelete={handleDelete}
                onToggle={handleDeleteConfirmationToggle}
                toDelete={deleteConfirmationState.toDelete.map(x => x.subject)}
                isOpen={deleteConfirmationState.display}
            />
            <Row className={'mb-1'}>
                <GridHeader
                    gridEntities={props.title || 'Notes'}
                    deleteButtonId='deleteButton'
                    disableDelete={!deleteEnabled}
                    onDeleteClick={handleDeleteClick}
                    addButtonId='addButton'
                    onAddClick={handleAddClick}
                    addTooltipTarget={'addButton'}
                    deleteTooltipTarget={'deleteButton'}
                    tooltipEntity={'Note'}
                />
            </Row>
            <Row className='mb-1'>
                <Col>
                    <div className='ag-theme-balham' style={{ width: 'auto' }}>
                        <DataGrid
                            rows={props.data.map(x => { return { id: x.journalId, ...x } })}
                            checkboxSelection
                            onRowDoubleClick={handleSelect}
                            onRowSelectionModelChange={handleSelectionChanged}
                            autoHeight={true}
                            rowHeight={35}
                            initialState={{
                                pagination: { paginationModel: { pageSize: 25 } },
                            }}
                            columns={[
                                {
                                    headerName: 'Activity Date',
                                    field: 'activityDate',
                                    flex: 3,
                                    valueFormatter: (params) => params.value ? moment(params.value).format('MM/DD/YYYY') : '',
                                },
                                {
                                    headerName: 'Activity Type',
                                    field: 'journalTypeId',
                                    flex: 3,
                                    valueGetter: (params) => {
                                        const journalType = props.journalTypes.find(type => type.lookupId === params.row.journalTypeId);
                                        return journalType ? journalType.name : 'Unknown';
                                    },
                                },
                                {
                                    headerName: 'Status',
                                    field: 'journalStatusTypeId',
                                    flex: 3,
                                    valueGetter: (params) => {
                                        const journalStatusType = props.journalStatusTypes.find(type => type.lookupId === params.row.journalStatusTypeId);
                                        return journalStatusType ? journalStatusType.name : 'Unknown';
                                    },
                                },
                                {
                                    headerName: 'Subject',
                                    field: 'subject',
                                    flex: 3,
                                },
                                {
                                    headerName: 'Added By',
                                    field: 'addedBy',
                                    flex: 3,
                                },
                            ]}
                            sx={{
                                backgroundColor: 'white'
                            }}
                            getRowClassName={(params) => 'grid-row'}
                        />
                    </div>
                </Col>
            </Row>
            <Modal isOpen={showAddEntry} toggle={handleToggleModal}>
                <ModalHeader>Entry</ModalHeader>
                <InputGroup className='p-3'>
                    <InputGroupText>Subject:</InputGroupText>
                    <Input name='subject' value={subject || ''} required invalid={!subject} onChange={handleSubjectChange} />
                </InputGroup>
                <InputGroup className='p-3'>
                <InputGroupText>Activity Types:</InputGroupText>
                    <JournalTypes
                        dropdownPlaceholder='Select'
                        name='journalTypeId'
                        lookups={props.journalTypes}
                        onChange={handleOnChange}
                        value={journalTypeId}
                />
                </InputGroup>
                <InputGroup className='p-3'>
                <InputGroupText>Activity Status:</InputGroupText>
                <JournalTypes
                    dropdownPlaceholder='Select'
                    name='journalStatusTypeId'
                    lookups={props.journalStatusTypes}
                    onChange={handleOnChangeStatusType}
                    value={journalStatusTypeId}
                />
                </InputGroup>
                <ModalBody>
                    <ReactQuill name='body' value={body} onChange={handleBodyChange} />
                    <br/>
                    <Label style={{paddingLeft: '5px'}}>Activity Date:</Label>
                    <Input type='date' max="2999-12-31" name='activityDate' onChange={(e) => handleActivityDateChange(e)} value={moment(activityDate).format('YYYY-MM-DD')} />
                </ModalBody>
                <ModalFooter>
                    <Button type='submit' color='primary' onClick={handleSaveEntry} disabled={!subject || subject === '' || !journalTypeId || !journalStatusTypeId}>
                        Save
                    </Button>{' '}
                    <Button color='primary' onClick={handleToggleModal}>
                        Cancel
                    </Button>{' '}
                </ModalFooter>
            </Modal>
        </React.Fragment>
    );
};

export default Journal;